import { cn } from '@/lib/utils';
import React, { useEffect, useRef, useState } from 'react';

interface StickyScrollbarProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps content with a synchronized sticky horizontal scrollbar that remains
 * visible at the bottom of the viewport while scrolling vertically through content.
 *
 * This solves the UX issue where users need to scroll down to access the horizontal
 * scrollbar before they can scroll horizontally to see wide table content.
 */
export function StickyScrollbar({ children, className }: StickyScrollbarProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    const contentEl = contentRef.current;

    if (!contentEl) return;

    // Find the actual scrollable container (table-container div with overflow-x-auto)
    const scrollableContainer = contentEl.querySelector(
      '[data-slot="table-container"]',
    ) as HTMLDivElement;

    if (!scrollableContainer) return;

    // Find the actual table element to measure its true width
    const tableElement = scrollableContainer.querySelector(
      '[data-slot="table"]',
    ) as HTMLTableElement;

    if (!tableElement) return;

    // Check if horizontal scrolling is needed
    const checkScrollNeeded = () => {
      const needed =
        scrollableContainer.scrollWidth > scrollableContainer.clientWidth;

      setNeedsHorizontalScroll(needed);

      if (needed) {
        // Use the actual table element's width, not the container's scrollWidth
        setScrollbarWidth(tableElement.offsetWidth);
      }
    };

    // Initial check
    checkScrollNeeded();

    // Set up resize observer to detect when horizontal scroll becomes needed/unneeded
    const resizeObserver = new ResizeObserver(() => {
      checkScrollNeeded();
    });

    resizeObserver.observe(scrollableContainer);
    resizeObserver.observe(tableElement);

    // Set up mutation observer to detect content changes (rows added, cells modified, etc.)
    const mutationObserver = new MutationObserver(() => {
      checkScrollNeeded();
    });

    mutationObserver.observe(tableElement, {
      childList: true, // Watch for added/removed rows
      subtree: true, // Watch entire table tree
      attributes: true, // Watch for attribute changes (like style, class)
      attributeFilter: ['style', 'class', 'width'], // Only watch relevant attributes
    });

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  // Separate effect for scroll synchronization (only runs when sticky scrollbar exists)
  useEffect(() => {
    if (!needsHorizontalScroll) return;

    const contentEl = contentRef.current;
    const stickyScrollbarEl = stickyScrollbarRef.current;

    if (!contentEl || !stickyScrollbarEl) return;

    const scrollableContainer = contentEl.querySelector(
      '[data-slot="table-container"]',
    ) as HTMLDivElement;

    if (!scrollableContainer) return;

    // Sync scroll positions: content -> sticky scrollbar
    const syncToSticky = () => {
      if (stickyScrollbarEl) {
        stickyScrollbarEl.scrollLeft = scrollableContainer.scrollLeft;
      }
    };

    // Sync scroll positions: sticky scrollbar -> content
    const syncToContent = () => {
      if (scrollableContainer) {
        scrollableContainer.scrollLeft = stickyScrollbarEl.scrollLeft;
      }
    };

    // Set up scroll event listeners
    scrollableContainer.addEventListener('scroll', syncToSticky, {
      passive: true,
    });
    stickyScrollbarEl.addEventListener('scroll', syncToContent, {
      passive: true,
    });

    // Cleanup
    return () => {
      scrollableContainer.removeEventListener('scroll', syncToSticky);
      stickyScrollbarEl.removeEventListener('scroll', syncToContent);
    };
  }, [needsHorizontalScroll]);

  return (
    <div className={cn('relative', className)}>
      {/* Content wrapper */}
      <div ref={contentRef}>{children}</div>

      {/* Sticky horizontal scrollbar - only visible when horizontal scrolling is needed */}
      {needsHorizontalScroll && (
        <div
          ref={stickyScrollbarRef}
          className={cn(
            // Hide on mobile, show on medium screens and up
            'hidden md:block',
            // Sticky positioning at bottom of viewport (with spacing to keep visible)
            'sticky right-0 bottom-2 left-0',
            'z-10',
            // Scrollbar container
            'overflow-x-auto overflow-y-hidden',
            'h-3',
            // Match panel scrollbar styling
            '[&::-webkit-scrollbar]:h-2',
            '[&::-webkit-scrollbar-track]:bg-transparent',
            '[&::-webkit-scrollbar-thumb]:bg-border',
            '[&::-webkit-scrollbar-thumb]:rounded-full',
            '[&::-webkit-scrollbar-thumb]:border-2',
            '[&::-webkit-scrollbar-thumb]:border-transparent',
            '[&::-webkit-scrollbar-thumb]:bg-clip-padding',
            'hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50',
            '[scrollbar-width:thin]',
            '[scrollbar-color:var(--border)_transparent]',
            // Background to distinguish from content
            'bg-muted/80 backdrop-blur-sm',
          )}
        >
          {/* Inner div that creates the scrollable width */}
          <div
            style={{ width: `${scrollbarWidth}px` }}
            className="h-1"
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
